import { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { ContactShadows, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import './ProgressArt.css';

declare global {
  interface Window {
    __progressArtState?: {
      ready: boolean;
      progress: number;
      blockCount: number;
    };
  }
}

interface ProgressArtProps {
  onBack: () => void;
}

interface BlockSpec {
  id: string;
  position: [number, number, number];
  size: [number, number, number];
  color: string;
  revealAt: number;
}

const CYCLE_SECONDS = 11;

function smoothstep(edge0: number, edge1: number, value: number) {
  const t = Math.min(Math.max((value - edge0) / (edge1 - edge0), 0), 1);
  return t * t * (3 - 2 * t);
}

function timelineProgress(elapsed: number) {
  const loop = (elapsed % CYCLE_SECONDS) / CYCLE_SECONDS;
  return loop < 0.82 ? loop / 0.82 : 1;
}

function ProgressBlock({ block }: { block: BlockSpec }) {
  const meshRef = useRef<THREE.Mesh>(null);

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const progress = timelineProgress(clock.getElapsedTime());
    const reveal = smoothstep(block.revealAt, block.revealAt + 0.18, progress);
    const lift = (1 - reveal) * 0.22;

    mesh.scale.set(
      block.size[0] * (0.86 + reveal * 0.14),
      Math.max(block.size[1] * reveal, 0.001),
      block.size[2] * (0.86 + reveal * 0.14),
    );
    mesh.position.set(
      block.position[0],
      block.position[1] - (1 - reveal) * block.size[1] * 0.5 + lift,
      block.position[2],
    );
    const material = mesh.material as THREE.MeshStandardMaterial;
    material.opacity = reveal;
  });

  return (
    <mesh ref={meshRef} castShadow receiveShadow>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial
        color={block.color}
        transparent
        opacity={0}
        roughness={0.58}
        metalness={0.02}
      />
    </mesh>
  );
}

function ProgressScene() {
  const rigRef = useRef<THREE.Group>(null);
  const blocks = useMemo<BlockSpec[]>(() => [
    {
      id: 'rear-wall',
      position: [0.68, 1.24, -0.44],
      size: [1.64, 2.48, 0.56],
      color: '#dfe9df',
      revealAt: 0.16,
    },
    {
      id: 'left-base',
      position: [-1.28, 0.34, 0.38],
      size: [0.78, 0.68, 0.78],
      color: '#d5e2da',
      revealAt: 0.04,
    },
    {
      id: 'left-step',
      position: [-1.20, 0.93, 0.05],
      size: [0.82, 0.82, 0.70],
      color: '#dce8de',
      revealAt: 0.11,
    },
    {
      id: 'left-tower',
      position: [-1.18, 1.70, -0.08],
      size: [0.58, 2.18, 0.58],
      color: '#e6eee6',
      revealAt: 0.22,
    },
    {
      id: 'center-riser',
      position: [-0.28, 0.75, -0.02],
      size: [0.88, 1.10, 0.70],
      color: '#dce8df',
      revealAt: 0.28,
    },
    {
      id: 'center-tower',
      position: [-0.10, 1.55, -0.10],
      size: [0.54, 1.42, 0.54],
      color: '#e9f0e9',
      revealAt: 0.36,
    },
    {
      id: 'front-plinth',
      position: [-0.86, 0.23, 0.88],
      size: [0.84, 0.46, 0.72],
      color: '#cedbd3',
      revealAt: 0.44,
    },
    {
      id: 'right-bridge',
      position: [1.46, 0.23, 0.60],
      size: [1.06, 0.46, 0.52],
      color: '#dbe6df',
      revealAt: 0.52,
    },
    {
      id: 'right-node',
      position: [2.04, 0.30, 0.36],
      size: [0.58, 0.60, 0.58],
      color: '#e3ebe4',
      revealAt: 0.60,
    },
    {
      id: 'right-chip',
      position: [2.35, 0.38, 0.10],
      size: [0.28, 0.34, 0.36],
      color: '#f0f4ef',
      revealAt: 0.68,
    },
    {
      id: 'accent-cut',
      position: [2.21, 0.63, 0.08],
      size: [0.20, 0.10, 0.08],
      color: '#102126',
      revealAt: 0.76,
    },
  ], []);

  useFrame(({ clock }) => {
    const rig = rigRef.current;
    if (!rig) return;

    const progress = timelineProgress(clock.getElapsedTime());
    rig.rotation.y = -0.56 + Math.sin(progress * Math.PI * 2) * 0.025;
    rig.position.y = Math.sin(progress * Math.PI) * 0.035;

    window.__progressArtState = {
      ready: true,
      progress,
      blockCount: blocks.length,
    };
  });

  return (
    <group ref={rigRef}>
      <mesh position={[0, -0.055, 0]} receiveShadow>
        <boxGeometry args={[4.7, 0.11, 3.34]} />
        <meshStandardMaterial color="#128d94" roughness={0.68} metalness={0.04} />
      </mesh>
      <mesh position={[-0.16, -0.095, 0.10]} rotation={[0, 0.02, 0]} receiveShadow>
        <boxGeometry args={[4.9, 0.035, 3.5]} />
        <meshStandardMaterial color="#0a1d20" roughness={0.82} />
      </mesh>
      {blocks.map((block) => (
        <ProgressBlock key={block.id} block={block} />
      ))}
    </group>
  );
}

function ProgressStageStrip() {
  return (
    <div className="progress-stage-strip" aria-label="Progress stages">
      <div className="progress-stage-card active">
        <span>01</span>
        <strong>Blockout</strong>
      </div>
      <div className="progress-stage-card">
        <span>02</span>
        <strong>Mass</strong>
      </div>
      <div className="progress-stage-card">
        <span>03</span>
        <strong>Light</strong>
      </div>
      <div className="progress-stage-card">
        <span>04</span>
        <strong>Final</strong>
      </div>
    </div>
  );
}

export default function ProgressArt({ onBack }: ProgressArtProps) {
  const shareText = encodeURIComponent('Progress screenshots make the art feel alive.');
  const shareUrl = encodeURIComponent(`${window.location.origin}/progress-art`);
  const shareHref = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`;

  return (
    <div className="progress-art-page">
      <header className="progress-art-header">
        <div className="progress-art-title-block">
          <h1>Progress Artifact</h1>
          <span>Isometric build loop</span>
        </div>
        <div className="progress-art-actions">
          <a className="progress-share-button" href={shareHref} rel="noreferrer" target="_blank">
            Share on X
          </a>
          <button className="progress-back-button" onClick={onBack} type="button">
            Car Viewer
          </button>
        </div>
      </header>

      <main className="progress-art-shell" data-testid="progress-art-page">
        <section className="progress-art-stage" aria-label="Animated progress artwork">
          <Canvas
            shadows
            orthographic
            camera={{ position: [4.5, 3.7, 5.25], zoom: 120, near: 0.1, far: 100 }}
            gl={{ antialias: true, alpha: false }}
          >
            <color attach="background" args={['#121313']} />
            <ambientLight intensity={0.34} />
            <directionalLight
              castShadow
              color="#f7fff3"
              intensity={3.5}
              position={[-3.8, 6.2, 4.8]}
              shadow-mapSize={[2048, 2048]}
            />
            <directionalLight color="#5bbfc6" intensity={0.72} position={[3.8, 2.6, -4.2]} />
            <ProgressScene />
            <ContactShadows blur={2.4} far={5} opacity={0.34} position={[0, -0.12, 0]} scale={7} />
            <OrbitControls
              enableDamping
              enablePan={false}
              enableRotate
              enableZoom={false}
              target={[0.12, 0.84, 0.08]}
            />
          </Canvas>
          <div className="progress-art-vignette" />
          <ProgressStageStrip />
        </section>
      </main>
    </div>
  );
}
